import type { Deck, StudySession, AppSettings, Theme } from '../types';

const STORAGE_KEYS = {
  decks: 'flashcard_decks',
  sessions: 'flashcard_sessions',
  settings: 'flashcard_settings',
  theme: 'flashcard_theme',
} as const;

// Deck storage operations
function getDecks(): Deck[] {
  try {
    const data = localStorage.getItem(STORAGE_KEYS.decks);
    return data ? JSON.parse(data) : [];
  } catch {
    return [];
  }
}

function saveDecks(decks: Deck[]): void {
  localStorage.setItem(STORAGE_KEYS.decks, JSON.stringify(decks));
}

function getDeckById(id: string): Deck | undefined {
  return getDecks().find((deck) => deck.id === id);
}

function createDeck(deck: Omit<Deck, 'id' | 'createdAt' | 'updatedAt'>): Deck {
  const newDeck: Deck = {
    ...deck,
    id: `deck-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  const decks = getDecks();
  saveDecks([...decks, newDeck]);
  return newDeck;
}

function updateDeck(id: string, updates: Partial<Deck>): Deck | undefined {
  const decks = getDecks();
  const index = decks.findIndex((deck) => deck.id === id);
  if (index === -1) return undefined;

  decks[index] = { ...decks[index], ...updates, updatedAt: new Date().toISOString() };
  saveDecks(decks);
  return decks[index];
}

function deleteDeck(id: string): boolean {
  const decks = getDecks();
  const filtered = decks.filter((deck) => deck.id !== id);
  if (filtered.length === decks.length) return false;
  saveDecks(filtered);
  return true;
}

// Session storage operations
function getSessions(): StudySession[] {
  try {
    const data = localStorage.getItem(STORAGE_KEYS.sessions);
    return data ? JSON.parse(data) : [];
  } catch {
    return [];
  }
}

function saveSessions(sessions: StudySession[]): void {
  localStorage.setItem(STORAGE_KEYS.sessions, JSON.stringify(sessions));
}

function addSession(session: StudySession): void {
  const sessions = getSessions();
  sessions.push(session);
  saveSessions(sessions);
}

// Settings storage operations
function getSettings(): AppSettings {
  try {
    const data = localStorage.getItem(STORAGE_KEYS.settings);
    if (data) {
      const parsed = JSON.parse(data);
      return { ...getDefaultSettings(), ...parsed };
    }
  } catch {
    // fallback to defaults
  }
  return getDefaultSettings();
}

function saveSettings(settings: AppSettings): void {
  localStorage.setItem(STORAGE_KEYS.settings, JSON.stringify(settings));
}

function getDefaultSettings(): AppSettings {
  return {
    theme: 'system',
    language: 'en',
    aiProviders: [
      {
        id: 'groq',
        name: 'Groq',
        endpoint: '',
        apiKey: '',
        enabled: true,
        models: ['llama-3.1-8b-instant', 'llama-3.2-3b-preview', 'llama-3.1-70b-versatile'],
        selectedModel: 'llama-3.1-8b-instant',
        maxTokens: 4000,
        temperature: 0.7,
        serverKeyAvailable: true,
      },
    ],
    defaultStudySettings: {
      shuffle: true,
      showHints: true,
      autoAdvance: false,
      trackProgress: true,
      studyNewCards: true,
      studyReviewCards: true,
      cardsPerSession: 20,
      autoFlipEnabled: false,
      autoFlipDelay: 5,
      autoProgressEnabled: false,
      autoProgressDelay: 10,
    },
    autoSync: false,
    dataRetention: 365,
  };
}

// Theme storage
function getTheme(): Theme {
  try {
    const data = localStorage.getItem(STORAGE_KEYS.theme);
    if (data) return data as Theme;
  } catch {
    // fallback
  }
  return 'system';
}

function setTheme(theme: Theme): void {
  localStorage.setItem(STORAGE_KEYS.theme, theme);
}

// Export operations
function exportData(): string {
  const data = {
    decks: getDecks(),
    sessions: getSessions(),
    settings: getSettings(),
    exportedAt: new Date().toISOString(),
  };
  return JSON.stringify(data, null, 2);
}

function importData(json: string): { success: boolean; message: string } {
  try {
    const data = JSON.parse(json);
    if (data.decks) saveDecks(data.decks);
    if (data.sessions) saveSessions(data.sessions);
    if (data.settings) saveSettings(data.settings);
    return { success: true, message: 'Data imported successfully' };
  } catch (error) {
    return { success: false, message: `Import failed: ${(error as Error).message}` };
  }
}

// Clear all data
function clearAllData(): void {
  Object.values(STORAGE_KEYS).forEach((key) => localStorage.removeItem(key));
}

export const storage = {
  getDecks,
  saveDecks,
  getDeckById,
  createDeck,
  updateDeck,
  deleteDeck,
  getSessions,
  saveSessions,
  addSession,
  getSettings,
  saveSettings,
  getDefaultSettings,
  getTheme,
  setTheme,
  exportData,
  importData,
  clearAllData,
};

export default storage;
